/**
 * Labels, tones and grouping for the AI agents settings section. Pure, so it is tested.
 */
import { AI_CAPABILITIES, CAPABILITY_TIERS, TIER_LABEL, capabilityByName } from '@orgus/protocol';
import type { AiActivity, AiOutcome, Capability, CapabilityTier } from '@orgus/protocol';
import type { Tone } from '@orgus/ui';

export const OUTCOME_LABEL: Record<AiOutcome, string> = {
  ok: 'Done',
  error: 'Failed',
  refused: 'Refused',
  limited: 'Rate limited',
  invalid: 'Bad input',
};

/** Only what went wrong carries colour: a call that worked is grey, like finished work everywhere. */
export const OUTCOME_TONE: Record<AiOutcome, Tone> = {
  ok: 'neutral',
  error: 'failed',
  refused: 'waiting',
  limited: 'waiting',
  invalid: 'neutral',
};

export const TIER_TONE: Record<CapabilityTier, Tone> = {
  read: 'neutral',
  operate: 'accent',
  elevated: 'waiting',
  critical: 'failed',
};

export function tierLabel(t: CapabilityTier): string {
  return TIER_LABEL[t];
}

/** The catalogue grouped by tier, in tier order. */
export function capabilitiesByTier(caps: readonly Capability[] = AI_CAPABILITIES): { tier: CapabilityTier; caps: Capability[] }[] {
  return CAPABILITY_TIERS.map((tier) => ({ tier, caps: caps.filter((c) => c.tier === tier) }));
}

/** Escalations that raise a capability into a tier, listed under that tier. */
export function escalationsInto(tier: CapabilityTier, caps: readonly Capability[] = AI_CAPABILITIES): { cap: Capability; reason: string }[] {
  const out: { cap: Capability; reason: string }[] = [];
  for (const c of caps) for (const e of c.escalations ?? []) if (e.tier === tier) out.push({ cap: c, reason: e.reason });
  return out;
}

/** "Read a session transcript", or the raw name when the catalogue does not know it. */
export function activityTitle(a: Pick<AiActivity, 'capability'>): string {
  return capabilityByName(a.capability)?.title ?? a.capability;
}

/** Who, without the agent: prefix the UI already implies. */
export function actorName(id: string): string {
  return id.startsWith('agent:') ? id.slice(6) : id;
}

/** "214 reads, 6 changes, 1 refused" with the zero parts left out. */
export function todaySummary(a: { readsToday: number; actionsToday: number; refusedToday: number }): string {
  const parts: string[] = [];
  if (a.readsToday) parts.push(`${a.readsToday} ${a.readsToday === 1 ? 'read' : 'reads'}`);
  if (a.actionsToday) parts.push(`${a.actionsToday} ${a.actionsToday === 1 ? 'change' : 'changes'}`);
  if (a.refusedToday) parts.push(`${a.refusedToday} refused`);
  return parts.length ? parts.join(', ') : 'Nothing today';
}

/**
 * The detail line under an activity row, without what the row already says. The hub's refusal
 * repeats the capability and the outcome ("Run a command was refused. Run a command is a
 * critical capability, and only a person may use those."); the row shows both already, so only
 * the reason is kept ("Only a person may use critical capabilities").
 */
export function activityDetail(a: Pick<AiActivity, 'capability' | 'message'>): string | null {
  const title = activityTitle(a);
  let m = (a.message ?? '').trim();
  if (!m) return null;
  const lead = `${title} was refused.`;
  if (m.startsWith(lead)) m = m.slice(lead.length).trim();
  for (const tier of ['critical', 'elevated']) {
    if (m.replace(/\.$/, '') === `${title} is ${tier === 'elevated' ? 'an' : 'a'} ${tier} capability, and only a person may use those`) return `Only a person may use ${tier} capabilities`;
  }
  return m || null;
}
